import React from 'react';
import { Text, Heading } from '@/components/ui/design-system';

export const ShippingPolicyContent = () => (
  <>
    <Heading level={2} className="heading-underline-animated pb-2 block">Introduction</Heading>
    <Text size="lg" className="mt-4 mb-5">
      This Shipping Policy explains how PAVISHA ("we", "us", or "our") processes and delivers 
      orders to our customers. By placing an order with us, you agree to be bound by this 
      Shipping Policy. We recommend that you read this Shipping Policy carefully before placing 
      an order.
    </Text>

    <Heading level={2} className="heading-underline-animated pb-2 block">Order Processing</Heading>
    <Text size="lg" className="mt-4 mb-5">
      Upon receiving your order, we aim to process it within 24-48 hours, excluding weekends 
      and holidays. During peak seasons or promotional periods, order processing may take an 
      additional 1-2 business days.
    </Text>
    <Text size="lg" className="mt-4 mb-5">
      Once your order has been processed and approved, you will receive an email confirmation 
      with your order details and a tracking number when available.
    </Text>

    <Heading level={2} className="heading-underline-animated pb-2 block">Shipping Methods and Timeframes</Heading>
    <Text size="lg" className="mt-4 mb-5">
      We currently offer the following shipping methods:
    </Text>
    
    <Heading level={3} className="heading-underline-animated pb-2 block mt-8">Standard Shipping</Heading>
    <Text size="lg" className="mt-4 mb-5">
      Our standard shipping service typically delivers within 5-7 business days, depending on 
      the delivery location. Standard shipping is available for all orders within India.
    </Text>
    
    <Heading level={3} className="heading-underline-animated pb-2 block mt-8">Express Shipping</Heading>
    <Text size="lg" className="mt-4 mb-5">
      For customers who need their orders more quickly, we offer express shipping, which delivers 
      within 2-3 business days. Express shipping is available for select locations within India.
    </Text>
    
    <Heading level={3} className="heading-underline-animated pb-2 block mt-8">Bulk Orders</Heading>
    <Text size="lg" className="mt-4 mb-5">
      For bulk orders or customized products, shipping timeframes will be communicated at the time 
      of quotation and may vary based on order volume and product specifications.
    </Text>

    <Heading level={2} className="heading-underline-animated pb-2 block">Shipping Costs</Heading>
    <Text size="lg" className="mt-4 mb-5">
      Shipping costs are calculated based on the weight of your order, delivery location, and 
      the shipping method you select during checkout. The exact shipping cost will be displayed 
      at checkout before you complete your payment.
    </Text>
    <Text size="lg" className="mt-4 mb-5">
      For orders above ₹5000, we offer free standard shipping within India. This offer may not 
      apply to bulk orders, custom products, or orders shipping to remote areas.
    </Text>

    <Heading level={2} className="heading-underline-animated pb-2 block">International Shipping</Heading>
    <Text size="lg" className="mt-4 mb-5">
      We currently ship to select international destinations. International shipping times vary 
      depending on the destination country, customs processing, and local delivery conditions. 
      Typical delivery timeframes are 10-20 business days.
    </Text>
    <Text size="lg" className="mt-4 mb-5">
      Please note that international shipments may be subject to customs duties and taxes imposed 
      by the destination country. These additional charges are the responsibility of the recipient 
      and are not included in our shipping costs.
    </Text>

    <Heading level={2} className="heading-underline-animated pb-2 block">Order Tracking</Heading>
    <Text size="lg" className="mt-4 mb-5">
      Once your order has been shipped, you will receive an email with tracking information. 
      You can use this tracking number to monitor the status of your shipment on our website 
      or directly through the carrier's tracking system.
    </Text>
    <Text size="lg" className="mt-4 mb-5">
      If you have not received a tracking number within 3-4 business days after receiving your 
      order confirmation, please contact our customer service team.
    </Text>

    <Heading level={2} className="heading-underline-animated pb-2 block">Shipping Restrictions</Heading>
    <Text size="lg" className="mt-4 mb-5">
      Certain remote or difficult-to-access areas may have limited delivery options or extended 
      delivery timeframes. In such cases, our customer service team will contact you to discuss 
      alternative arrangements.
    </Text>
    <Text size="lg" className="mt-4 mb-5">
      We reserve the right to refuse shipment to any location where we cannot ensure safe and 
      timely delivery of your products.
    </Text>

    <Heading level={2} className="heading-underline-animated pb-2 block">Delivery Issues</Heading>
    
    <Heading level={3} className="heading-underline-animated pb-2 block mt-8">Failed Deliveries</Heading>
    <Text size="lg" className="mt-4 mb-5">
      If a delivery attempt is unsuccessful, the shipping carrier will typically leave a notice 
      with instructions for rescheduling the delivery or collecting the package from a local 
      depot. If the package is returned to us due to multiple failed delivery attempts, you may 
      be responsible for additional shipping costs to reship the order.
    </Text>
    
    <Heading level={3} className="heading-underline-animated pb-2 block mt-8">Lost or Damaged Packages</Heading>
    <Text size="lg" className="mt-4 mb-5">
      If your package appears to be lost or damaged during transit, please contact our customer 
      service team within 48 hours of the expected delivery date. We will work with the shipping 
      carrier to resolve the issue.
    </Text>
    <Text size="lg" className="mt-4 mb-5">
      For packages that are visibly damaged upon delivery, please refuse the delivery if possible 
      and contact us immediately. If you've already accepted the package, please take photos of 
      the damaged package and contents to support your claim.
    </Text>

    <Heading level={2} className="heading-underline-animated pb-2 block">Address Changes</Heading>
    <Text size="lg" className="mt-4 mb-5">
      If you need to change your shipping address after placing an order, please contact our 
      customer service team as soon as possible. We will do our best to accommodate your request 
      if the order has not yet been shipped. Once an order has been shipped, we cannot change the 
      delivery address.
    </Text>

    <Heading level={2} className="heading-underline-animated pb-2 block">Shipping during Holidays</Heading>
    <Text size="lg" className="mt-4 mb-5">
      During holiday periods and national holidays, shipping times may be extended due to increased 
      volume and carrier schedules. We will provide notifications about holiday shipping schedules 
      on our website.
    </Text>

    <Heading level={2} className="heading-underline-animated pb-2 block">Changes to This Shipping Policy</Heading>
    <Text size="lg" className="mt-4 mb-5">
      We reserve the right to modify this Shipping Policy at any time. Changes and clarifications will 
      take effect immediately upon posting on the website. We encourage you to check this page 
      periodically for updates.
    </Text>
    <Text size="lg" className="mt-4 mb-5">
      The date at the top of this page indicates when this Shipping Policy was last updated.
    </Text>
  </>
);
